// @flow

import React, {useState, useRef} from 'react';
import {graphql} from 'react-apollo';

import VaHelpText from './VaHelpText';
import {useVacClient} from './utility';
import {EVENTS_TO_LISTEN, VAC_EVENTS} from './constants';
import {INTENTS} from '../../molecules/VaFloating/constants';

import './VaFloating.less';
import {VaAuthQuery} from './vaQuery';

export {INTENTS} from './constants';

const {
    VAC_CREATE_FRAME_FAILED,
    VAC_INITIALIZED,
    VAC_START_FRAME_FAILED,
    VAC_WIDGET_CLOSED,
    VAC_WIDGET_OPENED,
    VAC_CONVERSATION_CREATED,
    VAC_CHAT_MESSAGE_SENT,
} = VAC_EVENTS;

type VaConfig = {
    channelOriginId: string,
};

type VaInfo = {
    config: VaConfig,
    metaData: any,
};

type AuthInfo = {
    chatbotAuthorizationCode: string,
    loading: boolean,
};
type Props = {
    autoOpen: boolean,
    chatWidgetCounter: number,
    conversationProperties: any,
    data: AuthInfo,
    eventsInfo: any,
    helpText?: string,
    isDesktop: boolean,
    intent: string,
    isLoading: boolean,
    onChatFrameOpenSuccess: (any, string) => void,
    onDismissHelpText: () => void,
    onError: (any, string, string) => void,
    onVaInitDone: (any, boolean) => void,
    onVaOpenClick: (any, {source: string}) => void,
    otherIntentProps: any,
    updateIsLoading: (boolean) => void,
    vaInfo: VaInfo,
};

const VaFloating = ({
    autoOpen,
    chatWidgetCounter,
    conversationProperties,
    data,
    eventsInfo,
    helpText: initialHelpText,
    isDesktop,
    intent,
    isLoading,
    onChatFrameOpenSuccess,
    onDismissHelpText,
    onError,
    onVaInitDone,
    onVaOpenClick,
    otherIntentProps,
    updateIsLoading,
    vaInfo,
}: Props) => {
    const [currentIntent, setCurrentIntent] = useState(null);
    const [isCancelSucceeded, setIsCancelSucceeded] = useState(false);
    const [isConversationCreated, setIsConversationCreated] = useState(false);
    const [isOpened, setIsOpened] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [helpText, setHelpText] = useState(initialHelpText);
    const [currentChatWidgetCounter, setCurrentChatWidgetCounter] = useState(0);
    const vacRef = useRef(null);
    const reservationUuid = otherIntentProps?.reservationUuid || '';
    const intentMessage = {intent, reservationUuid};
    const conversationContextOptions = {intentMessage, conversationProperties};

    const {vacClient} = useVacClient((errorMessage) =>
        onError(eventsInfo, reservationUuid, errorMessage)
    );
    const {VacContextProvider, VacFrame} = vacClient || {};
    let {chatbotAuthorizationCode: authToken, loading: isFetchingToken} = data;

    const onHelpTextClose = () => {
        setHelpText();
        if (onDismissHelpText) {
            onDismissHelpText();
        }
    };

    const updateLoaderStatus = (value) => {
        if (
            intent === INTENTS.REFUND_STATUS ||
            intent === INTENTS.VRBO_CANCEL
        ) {
            updateIsLoading(value);
        }
    };

    if (!authToken || !VacContextProvider || !VacFrame || isFetchingToken) {
        return null;
    }
    let clickSource = 'help';
    const {channelOriginId} = vaInfo.config;
    const isHelpTextShown = !!initialHelpText;
    const onHelpClick = () => {
        clickSource = 'overlay';
        if (vacRef?.current?.vac?.openChatWidget) {
            vacRef.current.vac.openChatWidget();
        }
    };

    const setConversationContext = () => {
        if (
            isConversationCreated &&
            (!currentIntent || intent !== currentIntent)
        ) {
            if (vacRef?.current?.vac?.setConversationContext) {
                vacRef.current.vac.setConversationContext(
                    conversationContextOptions,
                    true // send true for active conversation
                );
                setCurrentIntent(intent);
                if (isHelpTextShown && intent === INTENTS.VRBO_CANCEL) {
                    onHelpTextClose();
                }
            }
        }
    };

    if (chatWidgetCounter && chatWidgetCounter > currentChatWidgetCounter) {
        if (vacRef?.current?.vac?.openChatWidget) {
            vacRef.current.vac.openChatWidget();
        }
        setCurrentChatWidgetCounter(chatWidgetCounter);
        /* Switch intent from arrival to cancel */
        setConversationContext();
    }

    const eventBehaviors = {
        eventHandler: (e) => {
            if (e.type === VAC_INITIALIZED) {
                setIsInitialized(true);
                /* Do not fire virtual agent presented event for cancel */
                if (onVaInitDone && intent !== INTENTS.VRBO_CANCEL) {
                    onVaInitDone(eventsInfo, isHelpTextShown);
                }
            } else if (e.type === VAC_WIDGET_OPENED) {
                setIsOpened(true);
                setIsConversationCreated(true);
                updateLoaderStatus(false);
                if (onChatFrameOpenSuccess && intent !== INTENTS.VRBO_CANCEL) {
                    onChatFrameOpenSuccess(eventsInfo, reservationUuid);
                }
                if (onVaOpenClick) {
                    onVaOpenClick(eventsInfo, {source: clickSource});
                    clickSource = 'help';
                }
            } else if (e.type === VAC_WIDGET_CLOSED) {
                setIsOpened(false);
            } else if (e.type === VAC_CREATE_FRAME_FAILED) {
                updateLoaderStatus(false);
                onError(eventsInfo, reservationUuid, VAC_CREATE_FRAME_FAILED);
            } else if (e.type === VAC_START_FRAME_FAILED) {
                updateLoaderStatus(false);
                onError(eventsInfo, reservationUuid, VAC_START_FRAME_FAILED);
            } else if (e.type === VAC_CONVERSATION_CREATED) {
                /* Set initial intent of the VA */
                setConversationContext();
            } else if (e.type === VAC_CHAT_MESSAGE_SENT) {
                /**
                    Event for successful setConversationContext 
                    Fire succeeded event only once for cancel skill
                **/
                if (intent === INTENTS.VRBO_CANCEL && !isCancelSucceeded) {
                    if (onChatFrameOpenSuccess) {
                        onChatFrameOpenSuccess(eventsInfo, reservationUuid);
                        setIsCancelSucceeded(true);
                    }
                }
            }
        },
        subscribedEvents: EVENTS_TO_LISTEN,
    };

    // Toggle loader when VA is already in open state.
    if (isOpened && isLoading) {
        updateLoaderStatus(false);
    }

    return (
        <div className="va-floating">
            {helpText && isInitialized && !isOpened ? (
                <VaHelpText
                    helpText={helpText}
                    isDesktop={isDesktop}
                    onHelpClick={onHelpClick}
                    onHelpTextClose={onHelpTextClose}
                />
            ) : (
                ''
            )}
            <VacContextProvider
                authToken={authToken}
                channelOriginId={channelOriginId}
            >
                <VacFrame
                    authToken={authToken}
                    conversationProperties={conversationProperties}
                    eventBehaviors={eventBehaviors}
                    initializationBehaviors={{
                        modeOptions: {
                            mode: 'CREATE_CONVERSATION',
                        },
                        participantData: {
                            participantType: 'Traveler',
                        },
                    }}
                    initializationOptions={{
                        channelOriginId,
                    }}
                    ref={vacRef}
                    token={authToken}
                    uiBehaviors={{autoOpen, embedded: false}}
                />
            </VacContextProvider>
        </div>
    );
};

export default VaFloating;
const VaFloatingWithGraphQl = graphql(VaAuthQuery, {
    options: (ownProps) => ({
        variables: {
            chatbotAuthInput: ownProps.chatbotAuthInput,
        },
    }),
})(VaFloating);
export {VaFloatingWithGraphQl as VaFloating};
